import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChildren } from '@angular/core';
import { OrderFulfillmentService } from '@vendasta/order-fulfillment';
import { ActivationStatus, LineItem } from '@vendasta/sales-orders';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import {
  MenuItemClickedInterface,
  MenuItemEventType,
} from './package-dropdown/package-dropdown-item/package-dropdown-item.component';
import { SalesOrderPackageDropdownComponent } from './package-dropdown/package-dropdown.component';
import { SalesOrderContentsService, UIAddons, UIPackages, UIProducts } from './sales-order-contents.service';

import { MatExpansionPanel } from '@angular/material/expansion';
import { FailureReason } from '@vendasta/order-fulfillment';
import { ActivationFailureDetails } from '@vendasta/order-fulfillment/lib/_internal/objects';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'sales-ui-sales-order-contents',
  templateUrl: './sales-order-contents.component.html',
  styleUrls: ['./sales-order-contents.component.scss'],
  providers: [SalesOrderContentsService],
  standalone: false,
})
export class SalesOrderContentsComponent implements OnInit, OnDestroy {
  @Input() compactView;
  @Input() lineItems: LineItem[] = [];
  @Input() partnerId: string;
  @Input() marketId: string;
  @Input() orderId: string;
  @Input() businessId: string;
  @Input() showMenuItems = false;

  @Output() loading: EventEmitter<boolean> = new EventEmitter();

  @ViewChildren(SalesOrderPackageDropdownComponent) private readonly packageDropdowns;
  @ViewChildren(MatExpansionPanel) private readonly topExpansionPanels;
  uiPackages$: Observable<UIPackages>;
  uiProducts$: Observable<UIProducts>;
  uiAddons$: Observable<UIAddons>;
  loading$: Observable<boolean> = this.contentsService.loading$;
  subscriptions: Subscription[] = [];

  constructor(
    private readonly contentsService: SalesOrderContentsService,
    private readonly orderFulfillmentService: OrderFulfillmentService,
  ) {
    this.subscriptions.push(this.loading$.pipe(tap((loading) => this.loading.emit(loading))).subscribe());
  }

  ngOnInit(): void {
    this.contentsService.initialize(this.partnerId, this.marketId, this.businessId, this.orderId, this.lineItems);

    const packageActivationErrors$ = this.contentsService.uiPackages$.pipe(
      switchMap((uiPackages) => {
        const orderItemIDs = (uiPackages.status || [])
          .filter((status) => status.status === ActivationStatus.ERRORED)
          .map((status) => status.uniqueId);
        if (!orderItemIDs.length) {
          return of([] as ActivationFailureDetails[]);
        }
        return this.orderFulfillmentService.getMultiActivationErrorsForOrderItems(orderItemIDs).pipe(
          take(1),
          catchError(() => of([] as ActivationFailureDetails[])),
        );
      }),
      map((errorDetails) => errorDetails || []),
    );

    const productActivationErrors$ = this.contentsService.uiProducts$.pipe(
      switchMap((uiProducts) => {
        const orderItemIDs = (uiProducts.status || [])
          .filter((status) => status.status === ActivationStatus.ERRORED)
          .map((status) => status.uniqueId);
        if (!orderItemIDs.length) {
          return of([] as ActivationFailureDetails[]);
        }
        return this.orderFulfillmentService.getMultiActivationErrorsForOrderItems(orderItemIDs).pipe(
          take(1),
          catchError(() => of([] as ActivationFailureDetails[])),
        );
      }),
      map((errorDetails) => errorDetails || []),
    );

    const addonsActivationErrors$ = this.contentsService.uiAddons$.pipe(
      switchMap((uiAddons) => {
        const orderItemIDs = (uiAddons.status || [])
          .filter((status) => status.status === ActivationStatus.ERRORED)
          .map((status) => status.uniqueId);
        if (!orderItemIDs.length) {
          return of([] as ActivationFailureDetails[]);
        }
        return this.orderFulfillmentService.getMultiActivationErrorsForOrderItems(orderItemIDs).pipe(
          take(1),
          catchError(() => of([] as ActivationFailureDetails[])),
        );
      }),
      map((errorDetails) => errorDetails || []),
    );

    this.uiPackages$ = combineLatest([this.contentsService.uiPackages$, packageActivationErrors$]).pipe(
      map(([packages, errorDetails]) => {
        const errDetailsMap = errorDetails.reduce((prev, curr) => {
          prev.set(curr.orderItemId, curr);
          return prev;
        }, new Map<string, ActivationFailureDetails>());
        this.openDropdownOnError(errorDetails.length);
        packages.status.forEach((pckg) => {
          if (errDetailsMap.has(pckg.uniqueId)) {
            pckg.failureDetails = this.translateFailureDetails(errDetailsMap.get(pckg.uniqueId));
          }
        });

        return packages;
      }),
    );

    this.uiProducts$ = combineLatest([this.contentsService.uiProducts$, productActivationErrors$]).pipe(
      map(([products, errorDetails]) => {
        const errDetailsMap = errorDetails.reduce((prev, curr) => {
          prev.set(curr.orderItemId, curr);
          return prev;
        }, new Map<string, ActivationFailureDetails>());
        this.openDropdownOnError(errorDetails.length);
        products.status.forEach((prod) => {
          if (errDetailsMap.has(prod.uniqueId)) {
            prod.failureDetails = this.translateFailureDetails(errDetailsMap.get(prod.uniqueId));
          }
        });

        return products;
      }),
    );

    this.uiAddons$ = combineLatest([this.contentsService.uiAddons$, addonsActivationErrors$]).pipe(
      map(([addons, errorDetails]) => {
        const errDetailsMap = errorDetails.reduce((prev, curr) => {
          prev.set(curr.orderItemId, curr);
          return prev;
        }, new Map<string, ActivationFailureDetails>());
        this.openDropdownOnError(errorDetails.length);
        addons.status.forEach((addon) => {
          if (errDetailsMap.has(addon.uniqueId)) {
            addon.failureDetails = this.translateFailureDetails(errDetailsMap.get(addon.uniqueId));
          }
        });

        return addons;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
    this.contentsService.clearOrderStateCache();
  }

  private openDropdownOnError(errorCount: number): void {
    if (errorCount > 0) {
      this.signalToOpenAllDropdowns();
    }
  }

  public signalToOpenAllDropdowns(): void {
    if (this.compactView) {
      this.topExpansionPanels.forEach((panel) => panel.open());
    }
    // the top level panels need time to open before the inner panels render
    window.setTimeout(() => {
      this.packageDropdowns.forEach((section) => section.openAllDropdowns());
    }, 750);
  }

  menuItemClick(event: MenuItemClickedInterface): void {
    if (event.eventType === MenuItemEventType.IGNORE_ACTIVATION_ERRORS) {
      this.contentsService.ignoreErrors(this.orderId, this.businessId, event.value).pipe(take(1)).subscribe();
    }
  }

  translateFailureDetails(activationFailureDetails: ActivationFailureDetails): string {
    switch (activationFailureDetails.failureReason) {
      case FailureReason.FAILURE_REASON_VALIDATION_ERROR:
        return this.translateValidationError(activationFailureDetails.details);
      case FailureReason.FAILURE_REASON_CHARGE_ERROR:
        return activationFailureDetails.details
          ? this.formattedChargeError(activationFailureDetails.details[0])
          : 'Payment could not be processed for this product';
      case FailureReason.FAILURE_REASON_VENDOR_REJECTED_ERROR:
        return activationFailureDetails.details ? activationFailureDetails.details[0] : 'Vendor rejected the order';
      case FailureReason.FAILURE_REASON_ACTIVATION_ERROR:
        return activationFailureDetails.details
          ? activationFailureDetails.details[0]
          : 'Product failed to activate. Contact Support for assistance.';
    }
    return activationFailureDetails.details ? activationFailureDetails.details[0] : '';
  }

  translateValidationError(details: string[]): string {
    let failureMsg = '';
    details.forEach((detail) => {
      const validationMsg = this.contentsService.validationErrorMsgs.has(detail)
        ? this.contentsService.validationErrorMsgs.get(detail)
        : '';
      if (failureMsg === '') {
        failureMsg += validationMsg;
      } else {
        failureMsg += ' / ' + validationMsg;
      }
    });
    return failureMsg;
  }

  formattedChargeError(details: string): string {
    const toCapitalize = details.split('_');
    toCapitalize.forEach((line) => {
      line.toUpperCase();
    });
    return toCapitalize.join(' ');
  }
}
